import { addDays } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router';
import { Info, ChevronUp, ChevronDown, X, Loader2 } from 'lucide-react';
import { Actions } from '../../store/actions/organisationActions';
import { Button } from '../ui/button';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { convertToTitleCase } from '../utils/GlobalFunctions';
import Select from 'src/components/ui/BrandSelect';
import _ from 'lodash';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../ui/tooltip';
import { Switch } from '../ui/switch';
import { BetaTag } from '../Common/ReusableComponents/Reusable';
import { hasUsableSubscription } from '../utils/subscriptionAccess';

const today = new Date();

function Modal({
  showModal,
  toggleModal,
  organisationDelete,
  deleteOrganisationMetric,
  action,
  saveOrganisation,
  organisationItem,
  modalItem,
  disableUser,
  orgId,
  creationDate,
  reinviteUser,
  branding,
  facilitiesList,
  updateUserPermissions,
  deleteOrganisationHierarchy,
}) {
  const [loading, setLoading] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [buttonsTitle, setButtonsTitle] = useState({ confirm: '', cancel: '' });
  const [role, setRole] = useState([]);
  const [showToggles, setShowToggles] = useState(true);
  const emptyPermissions = {
    organisation: {
      view: false,
      edit: false,
      manage: false,
    },
    organisationMetrics: {
      view: false,
      create: false,
      edit: false,
      delete: false,
    },
    facilities: {
      '*': {
        view: false,
        edit: false,
        delete: false,
      },
    },
    facilityMetrics: {
      view: false,
      create: false,
      edit: false,
      delete: false,
    },
    reports: {
      view: false,
      create: false,
    },
    usageData: {
      view: false,
      create: false,
      edit: false,
      delete: false,
    },
    dashboard: {
      view: false,
      create: false,
      edit: false,
      delete: false,
    },
    developer: {
      read: false,
      write: false,
    },
  };
  const [permissions, setPermissions] = useState(null);
  const history = useHistory();
  const font = branding?.fonts?.[0]?.font?.body;
  const { btnTextColor, primaryButton, tooltipBackground, tooltipText, primaryColor, secondaryColor, textColor } =
    branding?.colors || {};

  useEffect(() => {
    if (action === 'manageUser') {
      if (modalItem?.permissions) {
        setPermissions(modalItem?.permissions);
      } else {
        setPermissions(emptyPermissions);
      }
    }
  }, [modalItem, action]);

  const handleClose = () => {
    toggleModal(false, null);
    if (action === 'addingOrg') {
      history.push('/organisations');
    }
  };

  const handleAction = async () => {
    setLoading(true);
    if (action === 'addingOrg') {
      await saveOrganisation(organisationItem, history);
      toggleModal(false, null);
    } else if (action === 'disableUser') {
      await disableUser(modalItem.sub || modalItem.email);
    } else if (action === 'reinviteUser') {
      await reinviteUser(modalItem);
    } else if (action === 'deleteOrgMetric') {
      deleteOrganisationMetric(organisationDelete.item.id);
    } else if (action === 'deleteOrgHierarchy') {
      deleteOrganisationHierarchy(organisationItem.id);
      console.log('deleteOrgHierarchy');
    } else if (action === 'orgMissingCompulsoryData') {
      history.push(`/organisations/${orgId}`);
      toggleModal(false, null);
    } else if (action === 'clientTestOrg') {
      toggleModal(false, null);
    } else if (action === 'manageUser') {
      await updateUserPermissions(modalItem.sub, permissions);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (action === 'addingOrg') {
      setModalTitle('Do you wish to add a new organisation?');
      setButtonsTitle({ confirm: 'Yes, I want to subscribe', cancel: 'No, I don’t want to subscribe' });
    } else if (action === 'disableUser') {
      setModalTitle('Are you sure you want to delete this user?');
      setButtonsTitle({ confirm: 'Yes, delete the user', cancel: 'Cancel' });
    } else if (action === 'reinviteUser') {
      setModalTitle('Are you sure you want to reinvite this user?');
      setButtonsTitle({ confirm: 'Yes, reinvite', cancel: 'Cancel' });
    } else if (action === 'deleteOrgMetric') {
      setModalTitle('Are you sure you wish to delete this organisation metric?');
      setButtonsTitle({ confirm: 'Yes, delete this data', cancel: 'Cancel' });
    } else if (action === 'deleteOrgHierarchy') {
      setModalTitle('Are you sure you wish to delete this child organisation?');
      setButtonsTitle({ confirm: 'Yes, delete this child', cancel: 'Cancel' });
    } else if (action === 'orgMissingCompulsoryData') {
      setModalTitle('Your organisation is missing some compulsory data');
      setButtonsTitle({ confirm: 'Go to Organisation Details', cancel: '' });
    } else if (action === 'clientTestOrg') {
      setModalTitle('You are about to set this organisation to a Client Test Organisation');
      setButtonsTitle({ confirm: 'Close', cancel: '' });
    } else if (action === 'manageUser') {
      setModalTitle(`${modalItem?.name} ${modalItem?.surname}'s Permissions`);
      setButtonsTitle({ confirm: 'Save Changes', cancel: 'Close' });
    }
  }, [action]);

  const facilityOptions = useMemo(() => {
    const overriddenIds = new Set(Object.keys(permissions?.facilities || {}));
    overriddenIds.delete('*');

    return facilitiesList
      .filter((facility) => !overriddenIds.has(facility.id))
      .map((facility) => ({ value: facility.id, label: facility.name }));
  }, [facilitiesList, permissions?.facilities]);

  const handleFacilityPermissionChange = (facilityId, key, checked) => {
    setPermissions((prev) => ({
      ...prev,
      facilities: {
        ...prev.facilities,
        [facilityId]: {
          ...prev.facilities[facilityId],
          [key]: checked,
        },
      },
    }));
  };

  const addFacilityOverride = (selectedOption) => {
    if (!selectedOption) return;

    setPermissions((prev) => ({
      ...prev,
      facilities: {
        ...prev.facilities,
        [selectedOption.value]: {
          ...prev.facilities['*'],
        },
      },
    }));
  };

  const removeFacilityOverride = (facilityId) => {
    setPermissions((prev) => {
      const newFacilities = { ...prev.facilities };
      delete newFacilities[facilityId];
      return {
        ...prev,
        facilities: newFacilities,
      };
    });
  };

  const facilityAdmin = {
    facilities: { '*': ['view', 'edit', 'delete'] },
    facilityMetrics: ['view', 'create', 'edit', 'delete'],
  };

  const reportsAdmin = { reports: ['view', 'create'] };

  const usageAdmin = {
    usageData: ['view', 'create', 'edit', 'delete'],
  };

  const developer = { developer: ['read', 'write'] };

  const organisationAdmin = {
    organisation: ['view', 'edit', 'manage'],
    organisationMetrics: ['view', 'create', 'edit', 'delete'],
    facilities: { '*': ['view', 'edit', 'delete'] },
    facilityMetrics: ['view', 'create', 'edit', 'delete'],
    reports: ['view', 'create'],
    usageData: ['view', 'create', 'edit', 'delete'],
    dashboard: ['view', 'create', 'edit', 'delete'],
    developer: ['read', 'write'],
  };

  const viewOnly = {
    organisation: ['view'],
    organisationMetrics: ['view'],
    facilities: { '*': ['view'] },
    facilityMetrics: ['view'],
    reports: ['view'],
    usageData: ['view'],
    dashboard: ['view'],
  };

  const generalUser = {
    organisation: ['view'],
    organisationMetrics: ['view'],
    facilities: { '*': ['view', 'edit', 'delete'] },
    facilityMetrics: ['view', 'create', 'edit', 'delete'],
    reports: ['view', 'create'],
    usageData: ['view', 'create', 'edit', 'delete'],
    dashboard: ['view', 'create', 'edit', 'delete'],
  };

  const checkPermissions = (requiredPermissions, permissions) => {
    return Object.entries(requiredPermissions).every(([key, values]) => {
      if (Array.isArray(values)) {
        // Check if all required values exist and are true in permissions
        return values.every((value) => permissions[key]?.[value] === true);
      } else if (typeof values === 'object') {
        // Recursively check nested objects
        return checkPermissions(values, permissions[key] || {});
      }
      return false;
    });
  };

  useEffect(() => {
    if (action !== 'manageUser' || !permissions) return;

    setRole(() => {
      const next = [];
      if (checkPermissions(viewOnly, permissions)) next.push('View Only');
      if (checkPermissions(facilityAdmin, permissions)) next.push('Facility Admin');
      if (checkPermissions(reportsAdmin, permissions)) next.push('Reports Admin');
      if (checkPermissions(usageAdmin, permissions)) next.push('Usage Admin');
      if (checkPermissions(organisationAdmin, permissions)) {
        next.push('Organisation Admin');
      } else if (checkPermissions(generalUser, permissions)) {
        next.push('General User');
      }
      if (checkPermissions(developer, permissions)) next.push('Developer');
      return next;
    });
  }, [permissions, action]);

  const roleMappings = {
    'Organisation Admin': organisationAdmin,
    'Facility Admin': facilityAdmin,
    'Reports Admin': reportsAdmin,
    'Usage Admin': usageAdmin,
    'View Only': viewOnly,
    Developer: developer,
    'General User': generalUser,
  };

  const updatePermissionsRecursive = (permissionsStructure, permissionsObject, setValue) => {
    Object.entries(permissionsStructure).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        permissionsObject[key] = permissionsObject[key] || {};
        value.forEach((perm) => {
          permissionsObject[key][perm] = setValue;
        });
      } else if (typeof value === 'boolean') {
        permissionsObject[key] = setValue;
      } else if (typeof value === 'object') {
        permissionsObject[key] = permissionsObject[key] || {};
        updatePermissionsRecursive(value, permissionsObject[key], setValue);
      }
    });
  };

  const applyPermissionsTemplate = (templateKey) => {
    const newEditedState = _.cloneDeep(emptyPermissions);
    if (roleMappings[templateKey]) {
      updatePermissionsRecursive(roleMappings[templateKey], newEditedState, true);
    }
    setPermissions(newEditedState);
  };

  /** Organisation Admin and General User are mutually exclusive presets. */
  const handlePresetRoleClick = (presetKey) => {
    const otherRoles = role.filter((r) => r !== 'Organisation Admin' && r !== 'General User');

    if (role.includes(presetKey)) {
      const newEditedState = _.cloneDeep(permissions || emptyPermissions);
      if (roleMappings[presetKey]) {
        updatePermissionsRecursive(roleMappings[presetKey], newEditedState, false);
      }
      setRole(otherRoles);
      setPermissions(newEditedState);
      return;
    }

    setRole([...otherRoles, presetKey]);
    applyPermissionsTemplate(presetKey);
  };

  const handleOtherRoleChange = (event, newRole) => {
    const prevRole = role;
    setRole(newRole);

    const newEditedState = _.cloneDeep(permissions);

    newRole.forEach((selectedRole) => {
      if (roleMappings[selectedRole]) {
        updatePermissionsRecursive(roleMappings[selectedRole], newEditedState, true);
      }
    });

    if (newRole.includes('Usage Admin')) {
      newEditedState.facilities['*'].view = true;
    } else if (prevRole.includes('Usage Admin') && !newRole.includes('Usage Admin') && !newRole.includes('Facility Admin')) {
      newEditedState.facilities['*'].view = false;
    }

    if (prevRole.length > 0) {
      prevRole.forEach((selectedRole) => {
        if (!newRole.includes(selectedRole) && roleMappings[selectedRole]) {
          updatePermissionsRecursive(roleMappings[selectedRole], newEditedState, false);
        }
      });
    }

    setPermissions(newEditedState);
  };

  const expireDay = useMemo(() => {
    if (action === 'clientTestOrg') {
      if (!creationDate) {
        return addDays(new Date(), 30);
      } else {
        return addDays(new Date(creationDate), 30);
      }
    } else {
      return null;
    }
  }, [action, creationDate]);

  const Section = ({ title }) => (
    <div className='rounded-lg border border-border p-4'>
      <div className='font-semibold text-sm mb-3'>
        {title === 'dashboard' ? (
          <div className='flex items-center gap-1'>
            {convertToTitleCase(title)}
            <BetaTag backgroundColor={textColor} color={'#fff'} />
          </div>
        ) : (
          convertToTitleCase(title)
        )}
      </div>
      <div className='space-y-3'>
        {permissions &&
          Object.entries(permissions?.[title])
            ?.sort(([keyA], [keyB]) => switchOrder.indexOf(keyA) - switchOrder.indexOf(keyB))
            ?.map(([key, value], index) => (
              <div key={index} className='flex items-start gap-3'>
                <Switch
                  id={`${title}-${key}`}
                  onCheckedChange={(checked) => {
                    setPermissions((prevState) => ({ ...prevState, [title]: { ...prevState[title], [key]: checked } }));
                  }}
                  checked={value}
                  className='mt-0.5'
                />
                <div>
                  <label className='text-sm font-medium'>{convertToTitleCase(key)}</label>
                  {permissionDescriptions[title]?.[key] && (
                    <p className='text-xs text-muted-foreground mt-0.5' style={{ margin: 0, marginTop: '2px' }}>
                      {permissionDescriptions[title][key]}
                    </p>
                  )}
                </div>
              </div>
            ))}
      </div>
    </div>
  );

  const facilityStyle = permissions?.organisation?.manage
    ? {
        zIndex: 100,
        width: '100%',
        backgroundColor: '#BBB',
        height: '100%',
        opacity: 0.52,
        padding: 10,
        pointerEvents: 'none',
        borderRadius: '10px',
      }
    : { backgroundColor: 'transparent' };

  const FacilitySection = ({ title }) => (
    <div className='rounded-lg border border-border p-4' style={facilityStyle}>
      <div className='font-semibold text-sm mb-1'>{convertToTitleCase(title)}</div>
      <p className='text-xs text-muted-foreground mb-3' style={{ margin: 0, marginBottom: '12px' }}>
        {permissionDescriptions[title]}
      </p>
      <div
        className='inline-block text-xs font-medium px-2 py-0.5 rounded mb-2'
        style={{ backgroundColor: primaryColor, color: 'white' }}>
        Default Permissions
      </div>
      <div className='space-y-2'>
        {Object.entries(permissions?.['facilities']?.['*'] || {})
          ?.sort(([keyA], [keyB]) => switchOrder.indexOf(keyA) - switchOrder.indexOf(keyB))
          ?.map(([key, value]) => (
            <div key={key} className='flex items-center gap-3'>
              <Switch
                id={`facilities-*-${key}`}
                onCheckedChange={(checked) => {
                  handleFacilityPermissionChange('*', key, checked);
                }}
                checked={value}
              />
              <label className='text-sm font-medium'>{convertToTitleCase(key)}</label>
            </div>
          ))}
      </div>
      {facilityOptions?.length > 0 && (
        <div className='mt-3'>
          <Select
            options={facilityOptions}
            onChange={addFacilityOverride}
            placeholder='Add Facility Override'
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            menuPosition='fixed'
          />
        </div>
      )}
    </div>
  );

  const permissionDescriptions = {
    organisation: {
      view: 'Allow user to view organisation details',
      edit: 'Allow user to modify organisation details',
      manage: 'Grants full access. Allow user to create facilities, add new users and manage permissions, subscription, and group reporting',
    },
    organisationMetrics: {
      view: 'Allow user to view organisation metrics',
      create: 'Allow user to create organisation metrics',
      edit: 'Allow user to modify organisation metrics',
      delete: 'Allow user to delete organisation metrics',
    },
    reports: {
      view: 'Allow user to access and read reports',
      create: 'Allow user to generate reports',
    },
    usageData: {
      view: 'Allow user to view usage data',
      create: 'Allow user to create or duplicate usage data',
      edit: 'Allow user to modify usage data',
      delete: 'Allow user to delete usage data',
    },
    facilityMetrics: {
      view: 'Allow user to view facility metrics',
      create: 'Allow user to create facility metrics',
      edit: 'Allow user to modify facility metrics',
      delete: 'Allow user to remove facility metrics',
    },
    dashboard: {
      view: 'Allow user to view dashboard',
      create: 'Allow user to add dashboard elements',
      edit: 'Allow user to update dashboard elements',
      delete: 'Allow user to remove dashboard elements',
    },
    developer: {
      read: 'Enables read access of data from API endpoints including usage data, metrics, reports etc',
      write: 'Allow data modification (create/edit/delete) through API endpoints',
    },
    facilities: 'Default permissions apply to all facilities unless overridden by individual facility permissions',
  };

  const sortOrder = [
    'organisation',
    'organisationMetrics',
    'facilities',
    'reports',
    'usageData',
    'dashboard',
    'developer',
    'facilityMetrics',
  ];

  const switchOrder = ['view', 'create', 'edit', 'delete', 'manage', 'read', 'write'];

  const ModelContent = () => {
    if (action === 'addingOrg') {
      return (
        <p>
          By adding a new organisation to your account, you'll need to subscribe the added organisation to have access to our
          application.
        </p>
      );
    } else if (action === 'disableUser') {
      return <p>By confirming you are going to delete this user account.</p>;
    } else if (action === 'reinviteUser') {
      return <p>By confirming you are going to reinvite this user.</p>;
    } else if (action === 'deleteOrgMetric') {
      return <p>This action cannot be undone.</p>;
    } else if (action === 'deleteOrgHierarchy') {
      return <p>This action cannot be undone.</p>;
    } else if (action === 'orgMissingCompulsoryData') {
      return (
        <p>
          Your Organisation has some missing compulsory Data. <br /> To keep using the application please go to your
          organisation details page and fill the compuslory fields missing.
        </p>
      );
    } else if (action === 'clientTestOrg') {
      return (
        <p>
          Please note, by selecting this option, this organisation will only be retained for{' '}
          <b>30 days from the date it was created</b>. The organisation and all associated test data will be{' '}
          <b>
            permanently deleted at 11pm{' '}
            {expireDay > today ? (
              `on ${expireDay?.toLocaleDateString('en-GB')}`
            ) : (
              <span className='text-destructive font-bold'>TODAY</span>
            )}
          </b>
          . You can check how long the organisation has remaining by looking in the bottom left of the dashboard page (with
          the organisation selected).
        </p>
      );
    } else if (action === 'manageUser') {
      return (
        <div className='flex flex-col gap-4 text-sm'>
          <div className='flex items-center justify-between'>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className='inline-flex items-center gap-1.5 cursor-pointer'>
                    <b>Preset Roles</b>
                    <Info size={14} strokeWidth={1.5} style={{ color: primaryColor }} />
                  </span>
                </TooltipTrigger>
                <TooltipContent side='right'>
                  <span className='text-xs text-popover-foreground'>
                    Selecting a role will automatically toggle on/off the respective permissions associated with that role.
                  </span>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button
              id='reset-permissions'
              variant='outline'
              size='sm'
              onClick={() => {
                setPermissions(emptyPermissions);
                setRole([]);
              }}>
              Reset
            </Button>
          </div>

          <div className='flex flex-wrap justify-center gap-2'>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    id='OrganisationAdmin-toggle'
                    type='button'
                    onClick={() => handlePresetRoleClick('Organisation Admin')}
                    className='rounded-md text-sm font-semibold px-4 py-1.5 cursor-pointer border border-border'
                    style={{
                      fontFamily: font,
                      color: role.includes('Organisation Admin') ? btnTextColor : 'black',
                      backgroundColor: role.includes('Organisation Admin') ? primaryButton : '#DDD',
                    }}>
                    Organisation Manager
                  </button>
                </TooltipTrigger>
                <TooltipContent side='bottom'>
                  <span className='text-xs text-popover-foreground'>
                    Full administrative control over the organisation. Can manage users, billing, facility creation, and group reporting in addition to accessing all information across the platform.
                  </span>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    id='GeneralUser-toggle'
                    type='button'
                    onClick={() => handlePresetRoleClick('General User')}
                    className='rounded-md text-sm font-semibold px-4 py-1.5 cursor-pointer border border-border'
                    style={{
                      fontFamily: font,
                      color: role.includes('General User') ? btnTextColor : 'black',
                      backgroundColor: role.includes('General User') ? primaryButton : '#DDD',
                    }}>
                    General User
                  </button>
                </TooltipTrigger>
                <TooltipContent side='bottom'>
                  <span className='text-xs text-popover-foreground'>
                    Standard user with access to core platform features. Can modify usage data, facility information, and generate reports. View only access for organisation details and metrics.
                  </span>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <hr className='border-border' />

          <div className='text-center'>
            <b>Advanced Access Options</b>
          </div>
          <div className='flex flex-wrap justify-center gap-2'>
            {[
              { id: 'FacilityAdmin-toggle', value: 'Facility Admin', label: 'Facility Access', tip: 'Access to facility-related information. Can create, edit, and delete facilities and associated facility metrics.' },
              { id: 'ReportsAdmin-toggle', value: 'Reports Admin', label: 'Reports Access', tip: 'Can view existing reports and generate new ones.' },
              { id: 'UsageAdmin-toggle', value: 'Usage Admin', label: 'Usage Data Access', tip: 'Can access the usage data table with permissions to create, edit, and delete usage records.' },
              { id: 'ViewOnly-toggle', value: 'View Only', label: 'View All', tip: 'View only access to the platform. Can view the organisation and facility settings, as well as usage data and reports, but cannot make any changes.' },
              { id: 'Developer-toggle', value: 'Developer', label: 'Developer', tip: 'Access to developer tools. Intended for technical users who manage custom integrations.' },
            ].map((item) => (
              <TooltipProvider key={item.id}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      id={item.id}
                      onClick={(e) => {
                        const newRole = role.includes(item.value)
                          ? role.filter((r) => r !== item.value)
                          : [...role, item.value];
                        handleOtherRoleChange(e, newRole);
                      }}
                      className='rounded-md text-sm font-semibold px-4 py-1.5 cursor-pointer border border-border'
                      style={{
                        fontFamily: font,
                        color: role.includes(item.value) ? btnTextColor : 'black',
                        backgroundColor: role.includes(item.value) ? primaryButton : '#DDD',
                      }}>
                      {item.label}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side='bottom'>
                    <span className='text-xs text-popover-foreground'>{item.tip}</span>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
          </div>

          <hr className='border-border' />

          <div className='text-center'>
            <b className='cursor-pointer' onClick={() => setShowToggles(!showToggles)}>
              Configure Permissions{' '}
              {showToggles ? <ChevronUp size={16} strokeWidth={1.5} className='inline' /> : <ChevronDown size={16} strokeWidth={1.5} className='inline' />}
            </b>
          </div>
          <div
            style={{
              display: showToggles ? 'flex' : 'none',
              marginTop: 20,
              flexDirection: 'column',
              gap: 10,
              flexWrap: 'wrap',
            }}>
            {permissions &&
              Object.entries(permissions)
                ?.sort(([keyA], [keyB]) => sortOrder.indexOf(keyA) - sortOrder.indexOf(keyB))
                ?.map(([key]) => {
                  if (key !== 'facilities') {
                    return <Section key={key} title={key} />;
                  }
                })}

            <FacilitySection title='facilities' />
            <div className='space-y-2'>
              {permissions?.facilities &&
                Object.entries(permissions?.['facilities'] || {})
                  .filter(([id]) => id !== '*')
                  .map(([facilityId, facilityPermissions]) => {
                    const facility = facilitiesList.find((f) => f.id === facilityId);
                    if (!facility) return null;

                    return (
                      <div key={facilityId} className='rounded-lg border border-border p-4'>
                        <div className='flex items-center justify-between mb-2'>
                          <div className='flex items-center gap-2'>
                            <span
                              className='inline-block text-xs font-medium px-2 py-0.5 rounded'
                              style={{ backgroundColor: secondaryColor, color: 'white' }}>
                              {facility.name}
                            </span>
                          </div>
                          <button
                            className='text-muted-foreground hover:text-destructive'
                            title='Remove Override'
                            onClick={() => removeFacilityOverride(facilityId)}>
                            <X size={14} strokeWidth={1.5} />
                          </button>
                        </div>
                        <div className='space-y-2'>
                          {Object.entries(facilityPermissions || {})
                            ?.sort(([keyA], [keyB]) => switchOrder.indexOf(keyA) - switchOrder.indexOf(keyB))
                            ?.map(([key, value]) => (
                              <div key={key} className='flex items-center gap-3'>
                                <Switch
                                  checked={value}
                                  onCheckedChange={(checked) => handleFacilityPermissionChange(facilityId, key, checked)}
                                />
                                <label className='text-sm font-medium'>{convertToTitleCase(key)}</label>
                              </div>
                            ))}
                        </div>
                      </div>
                    );
                  })}
            </div>
          </div>
        </div>
      );
    }
  };

  if (!showModal) return null;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent
        onClose={handleClose}
        className={
          action === 'manageUser'
            ? 'flex max-h-[90vh] w-full max-w-2xl flex-col gap-0 overflow-hidden p-0 font-body'
            : 'flex max-h-[90vh] w-full max-w-lg flex-col gap-0 overflow-hidden p-0 font-body'
        }
        style={{ overflow: 'visible' }}>
        <DialogHeader className='shrink-0 space-y-0 border-b border-border px-6 pb-4 pt-6 pr-14 text-left'>
          <DialogTitle className='font-display text-lg leading-snug'>{modalTitle}</DialogTitle>
        </DialogHeader>

        <DialogBody className='min-h-0 flex-1 overflow-y-auto px-6 py-5 text-sm text-foreground'>
          <ModelContent />
        </DialogBody>

        <DialogFooter className='shrink-0 flex-col gap-3 border-t border-border bg-muted/30 px-6 py-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-center'>
          <span
            className={
              action === 'manageUser' && _.isEqual(permissions, emptyPermissions)
                ? 'flex items-start justify-center gap-2 text-center text-sm text-destructive'
                : 'hidden'
            }>
            <Info size={16} strokeWidth={1.5} className='mt-0.5 shrink-0' />
            <span>
              The current user has no permissions and will not be able to use the application
            </span>
          </span>
          <div className='flex w-full flex-row flex-wrap items-center justify-center gap-2'>
            {!!buttonsTitle.confirm && (
              <Button
                type='button'
                onClick={handleAction}
                id='Organisation-modal-confirm'
                disabled={loading || (action === 'manageUser' && _.isEqual(permissions, modalItem?.permissions))}
                className='shrink-0'>
                {loading && <Loader2 size={16} strokeWidth={1.5} className='mr-2 animate-spin' />}
                {buttonsTitle?.confirm}
              </Button>
            )}
            {!!buttonsTitle.cancel && (
              <Button type='button' variant='outline' onClick={handleClose} id='Organisation-modal-cancel' className='shrink-0'>
                {buttonsTitle?.cancel}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const mapStateToProps = (state) => {
  const { status: showModal, item: modalItem, action } = state.organisation?.modal || {};
  const { id: orgId, creationDate } = state.organisation?.details || {};
  const hasSubscriptionActiveToUse = hasUsableSubscription(state.organisation?.details);

  return {
    showModal,
    modalItem,
    organisationDelete: state.organisation?.modal,
    action,
    organisationItem: state.organisation?.modal?.item,
    hasSubscriptionActive: hasSubscriptionActiveToUse,
    orgId,
    creationDate,
    branding: state?.branding || {},
    facilitiesList: state?.facility?.list || [],
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    toggleModal: (status, item) => dispatch(Actions.toggleModal(status, item)),
    disableUser: (item) => dispatch(Actions.disableUser(item)),
    reinviteUser: (item) => dispatch(Actions.reinviteUser(item)),
    deleteOrganisationMetric: (id) => dispatch(Actions.deleteOrganisationMetric(id)),
    saveOrganisation: (organisation, history) => dispatch(Actions.saveOrganisation(organisation, history)),
    updateUserPermissions: (cognitoId, permissions) => dispatch(Actions.updateUserPermissions(cognitoId, permissions)),
    deleteOrganisationHierarchy: (id) => dispatch(Actions.deleteOrganisationHierarchy(id)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Modal);
